import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

// Role → capability scopes (MUST stay in sync with backend permissions.py::ROLE_SCOPES)
// Updated iter58: secretary/treasurer/president now see the full club (no admin rights).
const CMS_SCOPES = ['trips', 'articles', 'media', 'resources', 'about', 'climbing'];
const ALL_NON_ADMIN = [...CMS_SCOPES, 'members', 'operations', 'volunteer', 'finance', 'payments', 'expenses', 'inventory'];

export const ROLE_SCOPES = {
  admin:           [...ALL_NON_ADMIN, 'roles'],
  president:       [...ALL_NON_ADMIN],
  secretary:       [...ALL_NON_ADMIN],
  treasurer:       [...ALL_NON_ADMIN],
  // Iter408 — Ascents Officer also gets a read-only view of Finance + Expenses.
  expeditions_rep: [...CMS_SCOPES, 'operations', 'members', 'finance', 'expenses'],
  content_editor:  [...CMS_SCOPES],
  trip_leader:     ['operations'],
  instructor:      ['operations'],
  // Iter408 — Storekeeper also gets a read-only view of Operations (rosters).
  storekeeper:     ['inventory', 'operations'],
  member: [],
};

// Roles that hold a scope READ-ONLY (view/export, no create/update/delete).
// MUST stay in sync with backend permissions.py::READ_ONLY_ROLES.
export const READ_ONLY_ROLES = {
  operations: ['storekeeper'],
  finance: ['expeditions_rep'],
  expenses: ['expeditions_rep'],
};

export const isReadOnlyScope = (user, scope) =>
  !!user && (READ_ONLY_ROLES[scope] || []).includes(user.role);

// EN/GR label for the "staff tools" CTA shown to non-member roles in the header
// and the member-portal top bar. Unified to "Club management" so every role
// sees the same clear destination (the club dashboard).
export const STAFF_CTA_LABEL = {
  admin:           { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
  president:       { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
  secretary:       { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
  treasurer:       { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
  expeditions_rep: { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
  content_editor:  { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
  trip_leader:     { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
  instructor:      { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
  storekeeper:     { en: 'Club management', gr: 'Διαχείριση συλλόγου' },
};

// EN/GR proper-noun for the role identity (used in "You have access as {role}"
// sentences). Distinct from STAFF_CTA_LABEL which labels the tool/destination —
// here we want the person's title (Treasurer / Ταμίας, not Dashboard / Πίνακας).
export const STAFF_ROLE_LABEL = {
  admin:           { en: 'Administrator',   gr: 'Διαχειριστής' },
  president:       { en: 'President',       gr: 'Πρόεδρος' },
  secretary:       { en: 'Secretary',       gr: 'Γραμματέας' },
  treasurer:       { en: 'Treasurer',       gr: 'Ταμίας' },
  expeditions_rep: { en: 'Ascents Rep',     gr: 'Έφορος Αναβάσεων' },
  content_editor:  { en: 'Content Editor',  gr: 'Συντάκτης' },
  trip_leader:     { en: 'Trip Leader',     gr: 'Αρχηγός Εξόρμησης' },
  instructor:      { en: 'Instructor',      gr: 'Εκπαιδευτής' },
  storekeeper:     { en: 'Storekeeper',     gr: 'Αποθηκάριος' },
};

// Best landing route for a given role.
// Every admin-scoped role now lands on the club dashboard (/admin), which
// renders exactly the tiles their scope allows (others show locked). This
// keeps "Club management" consistent for all roles instead of dropping
// specialists straight into a single CMS/Operations surface.
export const ROLE_HOME = {
  admin:           '/admin',
  president:       '/admin',
  secretary:       '/admin',
  treasurer:       '/admin',
  expeditions_rep: '/admin',
  content_editor:  '/admin',
  trip_leader:     '/admin',
  instructor:      '/admin',
  storekeeper:     '/admin',
};

export const hasScope = (user, scope) => {
  if (!user) return false;
  const scopes = ROLE_SCOPES[user.role] || [];
  return scopes.includes(scope);
};

export const hasAnyAdminScope = (user) =>
  !!user && (ROLE_SCOPES[user.role] || []).length > 0;

// Iter232 — Mandatory-field checker for the "Complete your profile" wall.
// Google-sign-up creates a bare user (email + first/last name + avatar URL)
// and skips every other required field. Any authenticated route (except the
// completion page itself and logout/settings) must send such a user to
// `/complete-profile` before they can proceed.
const REQUIRED_TEXT_PATHS = [
  ['first_name'],
  ['last_name'],
  ['father_name'],
  ['date_of_birth'],
  ['profession'],
  ['mobile_phone'],
  ['address', 'street'],
  ['address', 'area'],
  ['emergency_contact', 'name'],
  ['emergency_contact', 'phone'],
];

const getPath = (obj, path) =>
  path.reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

export const isProfileComplete = (user) => {
  if (!user || !user.profile) return false;
  for (const p of REQUIRED_TEXT_PATHS) {
    const v = getPath(user.profile, p);
    if (v == null || String(v).trim() === '') return false;
  }
  // Photo required (uploaded via /api/auth/me/documents type=photo).
  const docs = user.documents || [];
  const hasPhoto = docs.some((d) => d && d.type === 'photo');
  if (!hasPhoto) return false;
  return true;
};

const ProtectedRoute = ({ children, requireAdmin = false, requireScope = null, allowIncompleteProfile = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500" data-testid="auth-loading">Loading…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  // Iter238 — Scope the profile-completeness wall to Google-only sign-ups.
  //
  // The wall was introduced in iter232 to stop Google-OAuth skeleton users
  // (which had no DOB / address / photo etc.) from wandering into the
  // portal with a half-baked profile. From iter234 onward brand-new Google
  // sign-ups are held in `pending_google_signups` until they finish the
  // wall, so a Google user in `users` is normally already complete.
  //
  // BUT — imported / legacy members were being caught by the same wall.
  // A member imported from the Excel roster has `auth_providers=["email"]`
  // and typically lacks DOB / profession / address / photo, so the check
  // in `isProfileComplete()` fails and they got trapped on the wall the
  // moment they signed in via "Forgot my password". From the imported
  // member's POV the site looked like it was demanding they re-register.
  //
  // Scoping the gate to Google-only users lets classic email accounts —
  // registered, imported, or admin-created — flow into the portal
  // unchanged. Any Google account that also linked email (via the
  // classic register form) is not gated either, since their profile
  // was fully collected during registration.
  if (
    !allowIncompleteProfile
    && (user.role === 'member' || !user.role)
    && !isProfileComplete(user)
    && (() => {
      const providers = Array.isArray(user.auth_providers) ? user.auth_providers : [];
      return providers.includes('google') && !providers.includes('email');
    })()
  ) {
    return <Navigate to="/complete-profile" replace />;
  }
  if (requireAdmin && !hasAnyAdminScope(user)) {
    return <Navigate to="/portal" replace />;
  }
  if (requireScope && !hasScope(user, requireScope)) {
    return <Navigate to="/portal" replace />;
  }
  return children;
};

export default ProtectedRoute;
